import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Trash2, ShieldCheck, Info, CreditCard, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LanguageSelector } from "@/components/LanguageSelector";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { LegalLinks } from "@/components/LegalLinks";

// Page publique de suppression de compte : https://chenia.app/delete-account
// (lien à déclarer dans Google Play Console ; accessible sans l'application)
const errKey = (e) => `del_err_${e?.response?.data?.detail || "generic"}`;

export default function DeleteAccount() {
  const { t, lang } = useLang();
  const { user, setUser, login } = useAuth();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", confirm: false });
  const [login_, setLogin] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const exchanged = useRef(false);

  // Retour de la reconnexion Google (#session_id=...) : on reste sur cette page
  useEffect(() => {
    const sid = new URLSearchParams((window.location.hash || "").replace(/^#/, "")).get("session_id");
    if (!sid || exchanged.current) return;
    exchanged.current = true;
    api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } })
      .then(({ data }) => { setUser(data); window.history.replaceState(null, "", "/delete-account"); })
      .catch(() => setError(t("del_err_reauth_required")));
  }, [setUser, t]);

  useEffect(() => {
    if (!user) { setInfo(null); return; }
    api.get("/account/deletion-info").then(({ data }) => setInfo(data)).catch(() => setError(t("del_err_generic")));
  }, [user, t]);

  // Connexion / reconnexion Google (projet Google Cloud CHENIA) : on reste sur cette page ;
  // le nouvel objet utilisateur relance le chargement de /account/deletion-info (effet ci-dessous)
  const onGoogle = (u) => { setError(""); setUser(u); };
  const onGoogleError = () => setError(t("del_err_reauth_required"));
  const doLogin = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await login(login_.email, login_.password); } catch (err) { setError(err.response?.data?.detail || t("del_err_generic")); }
    finally { setBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await api.post("/account/delete", { email: form.email, password: form.password || null, confirm: form.confirm });
      setDone(data);
      setUser(false);
    } catch (err) { setError(t(errKey(err))); }
    finally { setBusy(false); }
  };
  const plan = info ? t(`plan_${info.plan_tier}`) : "";

  return (
    <div className="min-h-screen bg-background" data-testid="delete-account-page">
      <header className="max-w-2xl mx-auto px-5 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><span className="grid place-items-center h-9 w-9 rounded-2xl bg-primary text-primary-foreground"><Leaf className="h-5 w-5" /></span><span className="font-serif text-xl font-semibold">CHENIA</span></Link>
        <LanguageSelector />
      </header>
      <main className="max-w-2xl mx-auto px-5 pb-16">
        <h1 className="font-serif text-3xl font-semibold tracking-tight" data-testid="delete-title">{t("del_title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("del_sub")}</p>

        {done ? (
          <section className="chenia-card p-6 mt-6" data-testid="delete-done">
            <CheckCircle2 className="h-8 w-8 text-primary" />
            <h2 className="font-serif text-2xl font-semibold mt-3">{t("del_done_title")}</h2>
            <p className="mt-2 text-sm">{t("del_done_desc", { date: new Date(done.deleted_at).toLocaleString(lang) })}</p>
            <p className="mt-2 font-mono text-sm rounded-xl bg-secondary px-3 py-2 inline-block" data-testid="delete-reference">{done.reference}</p>
            {done.subscription_canceled && <p className="mt-3 text-sm" data-testid="delete-sub-canceled">{t("del_done_sub_canceled")}</p>}
          </section>
        ) : (
          <>
            <section className="chenia-card p-6 mt-6" data-testid="delete-what">
              <h2 className="font-semibold flex items-center gap-2"><Trash2 className="h-4 w-4 text-primary" />{t("del_what_title")}</h2>
              <ul className="mt-3 space-y-1.5 text-sm list-disc pl-5">{[1, 2, 3, 4].map((i) => <li key={i}>{t(`del_what_${i}`)}</li>)}</ul>
              <p className="mt-3 text-sm font-medium text-chenia-terracottadark">{t("del_irreversible")}</p>
            </section>
            <section className="chenia-card p-6 mt-4" data-testid="delete-kept">
              <h2 className="font-semibold flex items-center gap-2"><Info className="h-4 w-4 text-primary" />{t("del_kept_title")}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{t("del_kept_desc")}</p>
            </section>
            <section className="chenia-card p-6 mt-4" data-testid="delete-subscription">
              <h2 className="font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" />{t("del_sub_title")}</h2>
              {info && <p className="mt-2 text-sm" data-testid="delete-sub-status">{info.plan_tier === "free" ? t("del_sub_free") : info.has_stripe_subscription ? t("del_sub_stripe", { plan }).replaceAll("{plan}", plan) : t("del_sub_paid_unknown", { plan })}</p>}
              <p className="mt-2 text-sm text-muted-foreground" data-testid="delete-sub-stores">{t("del_sub_stores")}</p>
              <p className="mt-2 text-sm text-muted-foreground">{t("del_timing")}</p>
            </section>

            <section className="chenia-card p-6 mt-4" data-testid="delete-identity">
              <h2 className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />{t("del_identity_title")}</h2>
              {!user ? (
                <>
                  <p className="mt-2 text-sm">{t("del_login_needed")}</p>
                  <form onSubmit={doLogin} className="mt-3 space-y-2" data-testid="delete-login-form">
                    <Input type="email" placeholder={t("email")} value={login_.email} onChange={(e) => setLogin({ ...login_, email: e.target.value })} data-testid="delete-login-email" />
                    <Input type="password" placeholder={t("password")} value={login_.password} onChange={(e) => setLogin({ ...login_, password: e.target.value })} data-testid="delete-login-password" />
                    <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="delete-login-submit">{t("del_login_btn")}</Button>
                  </form>
                  <div className="mt-2"><GoogleSignInButton onSuccess={onGoogle} onError={onGoogleError} text="signin_with" testId="delete-login-google" /></div>
                  <p className="mt-3 text-xs text-muted-foreground">{t("del_no_access")}</p>
                </>
              ) : info && (
                <form onSubmit={submit} className="mt-3 space-y-3" data-testid="delete-form">
                  <label className="block text-sm">{t("del_email_label")}
                    <Input type="email" autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="delete-email" />
                  </label>
                  {info.auth_provider === "email" ? (
                    <label className="block text-sm">{t("del_password_label")}
                      <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="delete-password" />
                    </label>
                  ) : info.auth_provider === "apple" ? (
                    <p className={"text-sm " + (info.apple_recent ? "text-primary" : "")} data-testid={info.apple_recent ? "delete-apple-ok" : "delete-apple-needed"}>{t(info.apple_recent ? "del_apple_ok" : "del_apple_needed")}</p>
                  ) : info.google_recent ? (
                    <p className="text-sm text-primary" data-testid="delete-google-ok">{t("del_google_ok")}</p>
                  ) : (
                    <div><p className="text-sm">{t("del_google_needed")}</p>
                      <div className="mt-2"><GoogleSignInButton onSuccess={onGoogle} onError={onGoogleError} text="signin_with" testId="delete-google-reauth" /></div></div>
                  )}
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" checked={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.checked })} className="mt-1" data-testid="delete-confirm" />
                    {t("del_confirm_check")}
                  </label>
                  <Button type="submit" disabled={busy || !form.confirm || !form.email || (info.auth_provider === "email" ? !form.password : info.auth_provider === "apple" ? !info.apple_recent : !info.google_recent)}
                    className="rounded-full w-full bg-destructive hover:bg-destructive/90 text-white" data-testid="delete-submit">
                    {busy ? t("del_deleting") : t("del_submit")}
                  </Button>
                </form>
              )}
              {error && <p className="mt-3 text-sm text-destructive" data-testid="delete-error">{error}</p>}
            </section>
          </>
        )}
        <LegalLinks className="mt-10 pt-6 border-t border-border" testIdPrefix="delete" />
      </main>
    </div>
  );
}
